import { BrandAndProduct } from '@api';
import { config } from '@terminal/config/app.config';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { SectionBase } from '@terminal/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import type { SharedTableColumn } from '@terminal/modules/overview-knowledge-graph/components/shared/shared-table/SharedTable';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { TabsType } from '@terminal/modules/tab-content/types';
import { useBrandsAndProducts } from '@terminal/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { useBrandAndProductDrawer } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import styles from './BrandsAndProductsSection.module.scss';

export interface BrandsAndProductsSectionProps {
  entityTag: string;
  source?: PosthogTrackingSource;
  isInDrawer?: boolean;
}

export const BrandsAndProductsSection = ({
  entityTag,
  source,
  isInDrawer = false,
}: BrandsAndProductsSectionProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { openDrawer } = useBrandAndProductDrawer({ entityTag, source });

  const {
    data: brandsAndProducts = [],
    isSuccess,
    isError,
    isLoading,
  } = useBrandsAndProducts(entityTag);

  const navigateToKnowledgeGraph = () => {
    navigateToPage({
      entityTag,
      source: source ?? POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      tab: TabsType.Connections,
      searchParams: getKnowledgeGraphSearchParams({
        entityTag,
        searchParams,
        subtab: KnowledgeGraphTabType.BrandsAndProducts,
      }),
    });
  };

  const topItems = [...brandsAndProducts]
    .sort((a, b) => a.relationship.rank - b.relationship.rank)
    .slice(0, 3)
    .map(item => ({ ...item, key: item.relationship.id }));

  const tableColumns: SharedTableColumn<BrandAndProduct>[] = [
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.name'),
      key: 'product',
      render: value => <div className={styles.title}>{value}</div>,
    },
  ];

  return (
    <SectionBase
      title={t('widget:assetOverview.knowledgeGraphRevamp.brandsAndProducts')}
      isError={isError}
      isEmpty={isSuccess && brandsAndProducts.length === 0}
      emptyTitle={t(
        'widget:assetOverview.knowledgeGraphRevamp.empty.brandsAndProducts'
      )}
      tableData={topItems}
      tableColumns={tableColumns}
      isFetching={isLoading}
      onExpand={navigateToKnowledgeGraph}
      onRowClick={item => {
        if (config.app.isTeamsApp) {
          return;
        }
        isInDrawer ? navigateToKnowledgeGraph() : openDrawer(item.product);
      }}
      hideExpandButton={config.app.isTeamsApp}
    />
  );
};
